import React, { FunctionComponent, useCallback, type MouseEvent } from 'react';
import { useAnalytics } from '@funtech-int/yango-shared';
import { TvChannel } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useIntl } from 'react-intl';

import { useI18n } from '@/shared/hooks/useI18n';
import * as CardKit from '@/shared/components/CardKit';
import { getImageUrlBySize, transformImage } from '@/shared/lib/images';
import { formatDuration } from '@/shared/lib/formatTime';

import { BasicCard } from '../../ui/Card/BasicCard';
import { useOnChannelCardClick } from '../../hooks/useOnChannelCardClick';
import { useCurrentChannelProgramData } from '../../hooks/useCurrentChannelProgramData';
import { createAnalyticsEvents } from '../../lib/createAnalyticsEvents';
import { channelsRoute } from '../../routes';

import styles from './ChannelCard.module.css';

const CHANNEL_SELECTION_LIST_LOGO_2x_URL_SIZE = '144x144';

interface Props {
    channel: TvChannel;
}

export const ChannelCardContainer: FunctionComponent<Props> = ({ channel }) => {
    const intl = useIntl();
    const i18n = useI18n();

    const { currentProgramData, refreshCurrentProgramData } = useCurrentChannelProgramData(channel);

    const isPurchased = Boolean(channel.viewOption?.isPurchased);

    const analytics = useAnalytics(createAnalyticsEvents);

    const onClick = useOnChannelCardClick(channel);
    const { currentTime, programName, totalTime, programType } = currentProgramData ?? {};

    const handleClick = useCallback(
        (e: MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();

            onClick();

            refreshCurrentProgramData();

            if (isPurchased) {
                analytics.cardClicked(channel.contentId, channel.title, programName ?? '');
            }
        },
        [
            analytics,
            channel.contentId,
            channel.title,
            isPurchased,
            onClick,
            programName,
            refreshCurrentProgramData,
        ]
    );

    const onShow = useCallback(
        () => analytics.cardShowed(channel.contentId, channel.title, programName ?? ''),
        [analytics, channel.contentId, channel.title, programName]
    );

    const { title } = channel;
    const img = getImageUrlBySize(
        transformImage(channel.logo),
        CHANNEL_SELECTION_LIST_LOGO_2x_URL_SIZE
    );
    return (
        <CardKit.Layout
            location={channelsRoute()}
            className={styles.root}
            onClick={handleClick}
            onShow={onShow}
        >
            <div className={styles.container}>
                <div className={styles.logoGroup}>
                    <div className={styles.logo} aria-hidden>
                        <BasicCard img={img} title={title} imgViewVariant="contain" />
                    </div>
                    <div className={styles.textWrapper}>
                        <p className={styles.channelTitleContainer}>
                            {/* button нужен для клавиатурной навигации по Tab, Enter и Space
                                onClick хендлится на корневом элементе компонента за счет всплытия события */}
                            <button className={styles.channelTitle} title={title}>
                                <bdi>{title}</bdi>
                            </button>
                            {!isPurchased && channel.viewOption?.subscriptionBadge && (
                                <CardKit.PurchaseBadge
                                    ariaLabel={i18n('card__purchasable')}
                                    badge={channel.viewOption.subscriptionBadge}
                                />
                            )}
                        </p>
                        <p className={styles.programName}>
                            <bdi>{programName}</bdi>
                        </p>
                        <p className={styles.programType}>{programType}</p>
                    </div>
                </div>
                <div className={styles.timeWrapper}>
                    <CardKit.Progress
                        value={currentTime ?? 0}
                        maxValue={totalTime ?? 0}
                        text={formatDuration(intl, totalTime ?? 0)}
                    />
                </div>
            </div>
        </CardKit.Layout>
    );
};
